import { Permission } from '@bookora/shared';

import type { PlannedTourKey, TourDef } from './types';

/**
 * Stubs of the tours declared ahead of their steps (see `PlannedTourKey`).
 *
 * A stub fixes what screens and links depend on — the key, the surface, the
 * route or inline host, the gate — and has NO steps, so: it is never offered,
 * its host renders nothing (`steps < minSteps`), it is in no hub list
 * (`TOUR_ORDER`) and `isTourKey()` refuses it, so a `?help=tour:` link to it
 * is ignored like any unknown key.
 *
 * Writing a tour = replacing its stub with a `tours/<key>.ts` file, moving the
 * key to `ShippedTourKey`, adding it to `TOUR_ORDER`, and checking the route
 * and gate below against the screen.
 */

function stub(
  def: Omit<TourDef, 'version' | 'autoOffer' | 'maxSteps' | 'minSteps' | 'steps'>,
): TourDef {
  return { ...def, version: 1, autoOffer: false, maxSteps: 5, minSteps: 2, steps: [] };
}

export const PLANNED_TOURS: Readonly<Record<PlannedTourKey, TourDef>> = {
  /**
   * «نهایی کردن» of a planned session. NOT a page tour, although it has a
   * URL (`/sessions/:id`): that route renders the sessions list with the
   * detail DRAWER open, and «نهایی کردن» lives in the drawer. The floating
   * runner hides under any overlay and ends after a minute of it, so a page
   * tour there could never show a step. It is an inline tour hosted by the
   * detail drawer (`useInlineTour('session-detail', …)` +
   * `<InlineTourSlot tour="session-detail" />`), and it needs a second anchor
   * in that drawer besides `session.finalize` to reach `minSteps`. Until then
   * the session form's status step carries the «نهایی کردن» sentence.
   */
  'session-detail': stub({
    key: 'session-detail',
    surface: 'inline',
    inlineHost: 'session-detail',
    title: 'نهایی کردن جلسه',
    hubDescription: 'جلسه درمان برنامه‌ریزی‌شده رو بعد از انجام، نهایی کنید.',
    requires: { all: [Permission.SESSION_EDIT] },
  }),
};

export function isPlannedTour(tour: TourDef): boolean {
  return tour.steps.length === 0;
}
